/* eslint-disable react-hooks/rules-of-hooks */

import { useRef } from 'react';

type ClickCallback = (event: Event) => void;

// Delay between a click event being recognised and the callback firing. This short
// time windows gives us time to receive a second click event in order to perform
// a different double-click action (and therefore cancelling the original click event)
// For example, zooming (double-click) vs opening a modal (single-click)
const CLICK_FIRE_DELAY = 300;

// Essentially ignore any double-click events after two single click events
// for this long (prevents double-firing)
const MINIMUM_DELAY_BETWEEN_DOUBLE_CLICKS = 100;

// Chrome on various devices has a bug whereby the first click is ignored for a short
// period after a touch interaction (see: https://bugs.chromium.org/p/chromium/issues/detail?id=1141207)
// This has the unfortunate side-effect of meaning a double-tap to zoom immediately
// after a slide transition causes only a single tap to be registered (which causes the
// single click event action to fire). This bug does not affect the double click event.
//
// Conversely, on pointer-enabled devices, a double-click event will not fire twice
// in succession meaning a double-tap to zoom in followed by another double-tap to
// zoom out will not register the second as an interaction. In this case, again, the single
// click action will be fired.
//
// Thus, this hook in all its convoluted logic is essentially just to allow both an onDoubleClick
// event and two successive onClick events to be both be interpreted as a double-click (with the first
// to fire preventing the other from doing so).
//
// Once Chrome's bug is fixed the onDoubleClick listener can be removed entirely.
const useZoomClickGestureHandlers =
    () =>
    (onClickCallback: ClickCallback, onDoubleClickCallback: ClickCallback) => {
        const state = useRef<{
            clickTimeoutId: number | null;
            lastDoubleClick: number;
        }>({
            clickTimeoutId: null,
            lastDoubleClick: Date.now()
        }).current;

        const fireClickEventAfterDelay = (event: Event) => {
            if (isFireClickEventPending()) {
                return;
            }

            state.clickTimeoutId = window.setTimeout(() => {
                onClickCallback(event);
                state.clickTimeoutId = null;
            }, CLICK_FIRE_DELAY);
        };

        const cancelPendingClickEvent = () => {
            if (state.clickTimeoutId) {
                clearTimeout(state.clickTimeoutId);
                state.clickTimeoutId = null;
            }
        };

        const isFireClickEventPending = () => state.clickTimeoutId !== null;
        const hasDoubleClickJustFired = () => {
            return (
                Date.now() - state.lastDoubleClick <
                MINIMUM_DELAY_BETWEEN_DOUBLE_CLICKS
            );
        };
        const recordDoubleClickFiringTimestamp = () =>
            (state.lastDoubleClick = Date.now());

        const onClick = ({ event }: { event: Event }) => {
            if (!isFireClickEventPending()) {
                fireClickEventAfterDelay(event);
                return;
            }

            // This is the second click in rapid succession so assume
            // double-click intent
            cancelPendingClickEvent();
            onDoubleClickCallback(event);
            recordDoubleClickFiringTimestamp();
        };

        const onDoubleClick = ({ event }: { event: Event }) => {
            if (!hasDoubleClickJustFired()) {
                onDoubleClickCallback(event);
                recordDoubleClickFiringTimestamp();
                cancelPendingClickEvent();
            }
        };

        return {
            onClick,
            onDoubleClick
        };
    };

export default useZoomClickGestureHandlers;
